/**
 * Lightweight i18n for EMA Kurer customer flow.
 *
 * Usage:
 *   import { useT, useLang } from "../lib/i18n";
 *   const t = useT();
 *   <h1>{t("home.hero.title")}</h1>
 *
 * - 2 languages: 'da' (default) + 'en'
 * - Stored in localStorage so user choice persists
 * - <html lang="..."> updated automatically
 * - Falls back to Danish key if English missing (= safe to ship partial)
 */
import React from "react";

const STORAGE_KEY = "ema_lang";

export const SUPPORTED = ["da", "en"];

const LangContext = React.createContext({ lang: "da", setLang: () => {} });

export function LangProvider({ children }) {
  const [lang, setLangState] = React.useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED.includes(stored)) return stored;
      // Auto-detect browser language on first visit (only if not Danish locale)
      const nav = (navigator.language || "da").toLowerCase();
      if (nav.startsWith("en")) return "en";
    } catch (e) {
      /* ignore */
    }
    return "da";
  });

  const setLang = React.useCallback((l) => {
    if (!SUPPORTED.includes(l)) return;
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch (e) {
      /* ignore */
    }
  }, []);

  React.useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
}

export function useLang() {
  return React.useContext(LangContext);
}

export function useT() {
  const { lang } = useLang();
  return React.useCallback(
    (key, vars) => {
      const dict = lang === "en" ? EN : DA;
      let v = key.split(".").reduce((o, k) => (o ? o[k] : undefined), dict);
      if (v === undefined && lang === "en") {
        // Graceful fallback to Danish
        v = key.split(".").reduce((o, k) => (o ? o[k] : undefined), DA);
      }
      if (typeof v !== "string") return key;
      if (vars) {
        Object.keys(vars).forEach((k) => {
          v = v.replace(new RegExp(`{${k}}`, "g"), vars[k]);
        });
      }
      return v;
    },
    [lang]
  );
}

// ---------- DICTIONARIES ----------

const DA = {
  nav: {
    home: "Forside",
    about: "Om EMA",
    services: "Services",
    pricing: "Priser",
    track: "Spor pakke",
    reviews: "Anmeldelser",
    business: "Erhverv",
    blog: "Blog",
    driver: "Bliv chauffør",
    subcontractor: "Bliv vognmand",
    contact: "Kontakt",
    book: "Book levering",
  },
  footer: {
    tagline: "Lokal kurér og erhvervslevering på Sjælland.",
    services: "Services",
    company: "Virksomhed",
    contact: "Kontakt",
    cities: "Kurer i din by",
    rights: "Reliable · Premium · Scandinavian",
  },
  home: {
    hero_kicker: "Lokal kurér · Sjælland",
    hero_title_a: "Lokal kurér",
    hero_title_b: "og erhvervslevering på Sjælland",
    hero_subtitle: "EMA Kurer hjælper private, butikker og webshops med Same Day, Next Day og aftenlevering på Sjælland. Book enkeltlevering online, eller få en erhvervsaftale med portal, labels, tracking, afhentning og månedsfakturering.",
    cta_book: "Book levering",
    cta_business: "Bliv erhvervskunde",
    cta_calculator: "Beregn pris",
    cta_track: "Spor min pakke",
    section_how: "Sådan virker det",
    section_why: "Hvorfor EMA Kurer",
    section_services: "Vores services",
    section_pricing: "Beregn pris nu",
    section_b2b: "Erhvervskurér på fast aftale",
    section_reviews: "Verificerede anmeldelser",
    section_app: "Hent appen",
  },
  services: {
    title: "Vores services",
    same_day: "Same-Day Kurér",
    express: "Express Kurér",
    evening: "Aftenlevering",
    business: "Erhvervskurér",
    from_price: "Fra {price} kr inkl. 8 km",
    per_extra_km: "derefter +6 kr/km",
    book: "Book nu",
  },
  booking: {
    title: "Book din kurer",
    step_pickup: "Afhentning",
    step_delivery: "Levering",
    step_details: "Detaljer",
    step_payment: "Betaling",
    pickup_address: "Afhentningsadresse",
    delivery_address: "Leveringsadresse",
    customer_name: "Dit navn",
    customer_email: "Din email",
    customer_phone: "Telefon",
    recipient_name: "Modtagers navn",
    recipient_phone: "Modtagers telefon",
    package_count: "Antal pakker",
    weight_kg: "Vægt (kg)",
    notes: "Bemærkninger",
    delivery_type: "Vælg leveringstype",
    pay_now: "Betal og book",
    total: "Total inkl. moms",
    discount_code: "Rabatkode",
    apply: "Anvend",
    pickup_date: "Afhentningsdato",
    pickup_time: "Afhentningstidspunkt",
  },
  track: {
    title: "Spor din pakke",
    subtitle: "Indtast booking-nummer og email",
    booking_number: "Booking-nummer",
    email: "Email",
    track_btn: "Spor pakke",
    no_booking: "Vi kunne ikke finde en booking med disse oplysninger",
    status_label: "Status",
    reorder_title: "Genbestil samme rute",
    reorder_btn: "Genbestil nu",
  },
  contact: {
    title: "Kontakt os",
    name: "Navn",
    email: "Email",
    phone: "Telefon",
    message: "Besked",
    send: "Send besked",
    sent: "Tak for din besked. Vi vender tilbage hurtigst muligt.",
  },
  about: {
    title: "Om EMA Kurer ApS",
    subtitle: "En premium kurérvirksomhed med direkte service og dansk dna",
  },
  reviews: {
    title: "Verificerede anmeldelser",
    write_one: "Skriv en anmeldelse",
  },
  common: {
    loading: "Indlæser...",
    error: "Der opstod en fejl",
    yes: "Ja",
    no: "Nej",
    cancel: "Annuller",
    save: "Gem",
    next: "Næste",
    back: "Tilbage",
    close: "Luk",
  },
};

const EN = {
  nav: {
    home: "Home",
    about: "About",
    services: "Services",
    pricing: "Pricing",
    track: "Track package",
    reviews: "Reviews",
    business: "Business",
    blog: "Blog",
    driver: "Become driver",
    subcontractor: "Carrier partners",
    contact: "Contact",
    book: "Book delivery",
  },
  footer: {
    tagline: "Local courier and business delivery on Zealand.",
    services: "Services",
    company: "Company",
    contact: "Contact",
    cities: "Courier in your city",
    rights: "Reliable · Premium · Scandinavian",
  },
  home: {
    hero_kicker: "Local courier · Zealand",
    hero_title_a: "Local courier",
    hero_title_b: "and business delivery on Zealand",
    hero_subtitle: "EMA Kurer helps private customers, shops and webshops with Same Day, Next Day and evening delivery on Zealand. Book a single delivery online, or get a business agreement with portal, labels, tracking, pickup and monthly invoicing.",
    cta_book: "Book delivery",
    cta_calculator: "Calculate price",
    cta_track: "Track my package",
    cta_business: "Become a business customer",
    section_how: "How it works",
    section_why: "Why EMA Kurer",
    section_services: "Our services",
    section_pricing: "Calculate price now",
    section_b2b: "Business courier with fixed agreement",
    section_reviews: "Verified reviews",
    section_app: "Get the app",
  },
  services: {
    title: "Our services",
    same_day: "Same-Day Courier",
    express: "Express Courier",
    evening: "Evening delivery",
    business: "Business courier",
    from_price: "From {price} DKK incl. 8 km",
    per_extra_km: "then +6 DKK/km",
    book: "Book now",
  },
  booking: {
    title: "Book your courier",
    step_pickup: "Pickup",
    step_delivery: "Delivery",
    step_details: "Details",
    step_payment: "Payment",
    pickup_address: "Pickup address",
    delivery_address: "Delivery address",
    customer_name: "Your name",
    customer_email: "Your email",
    customer_phone: "Phone",
    recipient_name: "Recipient name",
    recipient_phone: "Recipient phone",
    package_count: "Number of packages",
    weight_kg: "Weight (kg)",
    notes: "Notes",
    delivery_type: "Choose delivery type",
    pay_now: "Pay and book",
    total: "Total incl. VAT",
    discount_code: "Discount code",
    apply: "Apply",
    pickup_date: "Pickup date",
    pickup_time: "Pickup time",
  },
  track: {
    title: "Track your package",
    subtitle: "Enter booking number and email",
    booking_number: "Booking number",
    email: "Email",
    track_btn: "Track package",
    no_booking: "We couldn't find a booking with these details",
    status_label: "Status",
    reorder_title: "Reorder same route",
    reorder_btn: "Reorder now",
  },
  contact: {
    title: "Contact us",
    name: "Name",
    email: "Email",
    phone: "Phone",
    message: "Message",
    send: "Send message",
    sent: "Thanks for your message. We'll get back to you as soon as possible.",
  },
  about: {
    title: "About EMA Kurer ApS",
    subtitle: "A premium courier company with direct service and Danish DNA",
  },
  reviews: {
    title: "Verified reviews",
    write_one: "Write a review",
  },
  common: {
    loading: "Loading...",
    error: "An error occurred",
    yes: "Yes",
    no: "No",
    cancel: "Cancel",
    save: "Save",
    next: "Next",
    back: "Back",
    close: "Close",
  },
};
